import { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

// Platform theme presets
export const platformThemes = [
  { 
    id: "emerald", 
    name: "Emerald", 
    description: "Default green theme",
    primary: "#047857", 
    primaryLight: "#059669",
    primaryDark: "#065f46",
    accent: "#D4B068"
  },
  { 
    id: "ocean", 
    name: "Ocean Blue", 
    description: "Professional blue theme",
    primary: "#0369A1", 
    primaryLight: "#0284C7",
    primaryDark: "#075985",
    accent: "#F59E0B"
  },
  { 
    id: "purple", 
    name: "Royal Purple", 
    description: "Bold purple theme",
    primary: "#7C3AED", 
    primaryLight: "#8B5CF6",
    primaryDark: "#6D28D9",
    accent: "#EC4899"
  },
  { 
    id: "crimson", 
    name: "Crimson", 
    description: "Energetic red theme",
    primary: "#DC2626", 
    primaryLight: "#EF4444",
    primaryDark: "#B91C1C",
    accent: "#1F2937"
  },
  { 
    id: "slate", 
    name: "Slate", 
    description: "Modern gray theme",
    primary: "#475569", 
    primaryLight: "#64748B",
    primaryDark: "#334155",
    accent: "#0EA5E9"
  },
  { 
    id: "forest", 
    name: "Forest", 
    description: "Natural green theme",
    primary: "#166534", 
    primaryLight: "#15803D",
    primaryDark: "#14532D",
    accent: "#CA8A04"
  },
  { 
    id: "midnight", 
    name: "Midnight", 
    description: "Deep blue theme",
    primary: "#1E3A5F", 
    primaryLight: "#1E40AF",
    primaryDark: "#1E3A8A",
    accent: "#F97316"
  },
  { 
    id: "charcoal", 
    name: "Charcoal", 
    description: "Sleek dark theme",
    primary: "#374151", 
    primaryLight: "#4B5563",
    primaryDark: "#1F2937",
    accent: "#10B981"
  },
];

export function ThemeProvider({ children }) {
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Load from localStorage or default to emerald
    const saved = localStorage.getItem("ledgeramt-platform-theme");
    return saved ? JSON.parse(saved) : platformThemes[0];
  });

  // Apply theme to CSS variables
  useEffect(() => {
    const root = document.documentElement;
    
    // Convert hex to HSL for CSS variables
    const hexToHsl = (hex) => {
      let r = parseInt(hex.slice(1, 3), 16) / 255;
      let g = parseInt(hex.slice(3, 5), 16) / 255;
      let b = parseInt(hex.slice(5, 7), 16) / 255;

      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
          default: h = 0;
        }
      }

      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    // Set CSS variables
    root.style.setProperty("--primary", hexToHsl(currentTheme.primary));
    root.style.setProperty("--ring", hexToHsl(currentTheme.primary));
    
    // Update Tailwind primary colors as CSS custom properties
    root.style.setProperty("--theme-primary", currentTheme.primary);
    root.style.setProperty("--theme-primary-light", currentTheme.primaryLight);
    root.style.setProperty("--theme-primary-dark", currentTheme.primaryDark);
    root.style.setProperty("--theme-accent", currentTheme.accent);

    // Save to localStorage
    localStorage.setItem("ledgeramt-platform-theme", JSON.stringify(currentTheme));
  }, [currentTheme]);

  const setTheme = (themeId) => {
    const theme = platformThemes.find(t => t.id === themeId);
    if (theme) {
      setCurrentTheme(theme);
    }
  };

  const setCustomTheme = (primary, accent) => {
    // Calculate light and dark variants
    const adjustBrightness = (hex, percent) => {
      const num = parseInt(hex.slice(1), 16);
      const amt = Math.round(2.55 * percent);
      const R = Math.min(255, Math.max(0, (num >> 16) + amt));
      const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
      const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
      return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
    };

    setCurrentTheme({
      id: "custom",
      name: "Custom",
      description: "Your custom theme",
      primary,
      primaryLight: adjustBrightness(primary, 15),
      primaryDark: adjustBrightness(primary, -15),
      accent
    });
  };

  return (
    <ThemeContext.Provider value={{ 
      currentTheme, 
      setTheme, 
      setCustomTheme,
      themes: platformThemes
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
